import React, { useEffect, useRef, useState } from 'react';
import { IoClose } from 'react-icons/io5';
import Swal from 'sweetalert2';
import { Base_url } from '../../utils/Base_url';

const emptyBio = () => ({
  name: '',
  fatherName: '',
  permanentAddress: '',
  temporaryAddress: '',
  dateOfBirth: '',
  nationality: '',
  birthPlace: '',
  cast: '',
  maritalStatus: '',
  cnic: '',
  profession: '',
  phoneNumber: '',
  whatsappNumber: '',
  guardian: {
    name: '',
    fatherName: '',
    relationship: '',
    permanentAddress: '',
    temporaryAddress: '',
    cnic: '',
    phoneNumber: '',
    whatsappNumber: ''
  }
});

/**
 * View / edit investor bio data form.
 * @param {boolean} readOnly - when true, fields are display-only (no save)
 */
const BioDataModal = ({ isOpen, onClose, investorId, readOnly = false }) => {
  const [bioFormData, setBioFormData] = useState(emptyBio);
  const [isLoadingBio, setIsLoadingBio] = useState(false);
  const [isSavingBio, setIsSavingBio] = useState(false);
  const hasFetchedForCurrentId = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setBioFormData(emptyBio());
      setIsLoadingBio(false);
      setIsSavingBio(false);
      hasFetchedForCurrentId.current = null;
      return;
    }

    if (!investorId || hasFetchedForCurrentId.current === investorId) return;

    setIsLoadingBio(true);
    hasFetchedForCurrentId.current = investorId;

    const fetchBioData = async () => {
      try {
        const response = await fetch(
          `${Base_url}/v1/worker/bio-data-without-plan/${investorId}`
        );
        const result = await response.json();

        if (result.success && result.bioData) {
          const fetchedBio = result.bioData;
          if (fetchedBio.dateOfBirth) {
            fetchedBio.dateOfBirth = new Date(fetchedBio.dateOfBirth)
              .toISOString()
              .split('T')[0];
          }

          setBioFormData({
            name: fetchedBio.name || '',
            fatherName: fetchedBio.fatherName || '',
            permanentAddress: fetchedBio.permanentAddress || '',
            temporaryAddress: fetchedBio.temporaryAddress || '',
            dateOfBirth: fetchedBio.dateOfBirth || '',
            nationality: fetchedBio.nationality || '',
            birthPlace: fetchedBio.birthPlace || '',
            cast: fetchedBio.cast || '',
            maritalStatus: fetchedBio.maritalStatus || '',
            cnic: fetchedBio.cnic || '',
            profession: fetchedBio.profession || '',
            phoneNumber: fetchedBio.phoneNumber || '',
            whatsappNumber: fetchedBio.whatsappNumber || '',
            guardian: {
              name: fetchedBio.guardian?.name || '',
              fatherName: fetchedBio.guardian?.fatherName || '',
              relationship: fetchedBio.guardian?.relationship || '',
              permanentAddress: fetchedBio.guardian?.permanentAddress || '',
              temporaryAddress: fetchedBio.guardian?.temporaryAddress || '',
              cnic: fetchedBio.guardian?.cnic || '',
              phoneNumber: fetchedBio.guardian?.phoneNumber || '',
              whatsappNumber: fetchedBio.guardian?.whatsappNumber || ''
            }
          });
        } else {
          Swal.fire({
            title: 'Bio not available',
            text: result.message || 'Investor Bio not available.',
            icon: 'info',
            confirmButtonColor: '#00afef'
          });
          onClose();
        }
      } catch (error) {
        console.error('Error fetching bio data:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to fetch investor bio data. Please try again.',
          icon: 'error',
          confirmButtonColor: '#d33'
        });
        onClose();
      } finally {
        setIsLoadingBio(false);
      }
    };

    fetchBioData();
  }, [isOpen, investorId, onClose]);

  if (!isOpen) return null;

  if (isLoadingBio || (investorId && hasFetchedForCurrentId.current !== investorId)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 text-center w-full max-w-sm mx-4">
          <p className="text-gray-800">Loading Bio Data...</p>
        </div>
      </div>
    );
  }

  const handleBioFormChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setBioFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuardianChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setBioFormData((prev) => ({
      ...prev,
      guardian: { ...prev.guardian, [name]: value }
    }));
  };

  const handleSaveBio = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setIsSavingBio(true);

    try {
      const response = await fetch(
        `${Base_url}/v1/worker/bio-investor/${investorId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(bioFormData)
        }
      );
      const result = await response.json();

      if (result.success) {
        Swal.fire({
          title: 'Success!',
          text: result.message || 'Bio data updated successfully.',
          icon: 'success',
          confirmButtonColor: '#00afef'
        });
        onClose();
      } else {
        throw new Error(result.message || 'Failed to update bio data.');
      }
    } catch (error) {
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Failed to update bio data. Please try again.',
        icon: 'error',
        confirmButtonColor: '#d33'
      });
    } finally {
      setIsSavingBio(false);
    }
  };

  const fieldClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-700';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {readOnly ? 'Bio Data Form' : 'Edit Bio Data'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <IoClose size={24} />
          </button>
        </div>

        <form onSubmit={handleSaveBio} className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-700 mt-2 mb-2">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ['name', 'Name', 'text'],
              ['fatherName', "Father's Name", 'text'],
              ['permanentAddress', 'Permanent Address', 'text'],
              ['temporaryAddress', 'Temporary Address', 'text'],
              ['dateOfBirth', 'Date of Birth', 'date'],
              ['nationality', 'Nationality', 'text'],
              ['birthPlace', 'Birth Place', 'text'],
              ['cast', 'Cast', 'text']
            ].map(([name, label, type]) => (
              <div key={name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <input
                  type={type}
                  name={name}
                  value={bioFormData[name]}
                  onChange={handleBioFormChange}
                  disabled={readOnly}
                  className={fieldClass}
                />
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marital Status
              </label>
              <select
                name="maritalStatus"
                value={bioFormData.maritalStatus}
                onChange={handleBioFormChange}
                disabled={readOnly}
                className={fieldClass}
              >
                <option value="">Select status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
            </div>

            {[
              ['cnic', 'CNIC'],
              ['profession', 'Profession'],
              ['phoneNumber', 'Phone Number'],
              ['whatsappNumber', 'WhatsApp Number']
            ].map(([name, label]) => (
              <div key={name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <input
                  type="text"
                  name={name}
                  value={bioFormData[name]}
                  onChange={handleBioFormChange}
                  disabled={readOnly}
                  className={fieldClass}
                />
              </div>
            ))}
          </div>

          <h3 className="text-lg font-semibold text-gray-700 mt-6 mb-2">
            Guardian Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ['name', 'Guardian Name'],
              ['fatherName', "Guardian Father's Name"],
              ['relationship', 'Relationship'],
              ['permanentAddress', 'Guardian Permanent Address'],
              ['temporaryAddress', 'Guardian Temporary Address'],
              ['cnic', 'Guardian CNIC'],
              ['phoneNumber', 'Guardian Phone Number'],
              ['whatsappNumber', 'Guardian WhatsApp Number']
            ].map(([name, label]) => (
              <div key={`g-${name}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label}
                </label>
                <input
                  type="text"
                  name={name}
                  value={bioFormData.guardian[name]}
                  onChange={handleGuardianChange}
                  disabled={readOnly}
                  className={fieldClass}
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              disabled={isSavingBio}
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="px-4 py-2 bg-secondary text-white rounded-md hover:opacity-90 disabled:opacity-50"
                disabled={isSavingBio}
              >
                {isSavingBio ? 'Saving...' : 'Save Bio'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default BioDataModal;
